import { useState, useRef, useEffect, useMemo } from 'react';
import { foldText } from '../../../utils/fold';

const MAX_RESULTS = 50;

export default function PersonCell({ value, users = [], onChange, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const assigned = users.find((u) => u.id === value);
  // Nome dobrado pré-calculado por usuário (chave `users`), pra não refazer
  // o normalize/NFD a cada tecla digitada na busca.
  const foldedUsers = useMemo(
    () => users.map((u) => ({ ...u, _folded: foldText(u.name) })),
    [users]
  );
  const foldedSearch = foldText(search.trim());
  const matched = foldedSearch
    ? foldedUsers.filter((u) => u._folded.includes(foldedSearch))
    : foldedUsers;
  const filtered = matched.slice(0, MAX_RESULTS);
  const truncated = matched.length > MAX_RESULTS;

  const getInitials = (name) =>
    name ? name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';

  return (
    <div ref={ref} className="relative w-full h-full">
      <div
        onClick={() => { if (readOnly) return; setOpen(!open); setSearch(''); }}
        className={`w-full h-full flex items-center justify-center transition-colors min-h-[40px] ${readOnly ? '' : 'cursor-pointer hover:bg-white/5'}`}
      >
        {assigned ? (
          <div className="flex items-center gap-2">
            {assigned.avatar_url ? (
              <img src={assigned.avatar_url} className="w-6 h-6 rounded-full" alt={assigned.name} />
            ) : (
              <div className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[10px] font-semibold">
                {getInitials(assigned.name)}
              </div>
            )}
            <span className="text-sm text-text-primary truncate">{assigned.name}</span>
          </div>
        ) : (
          <span />
        )}
      </div>

      {open && !readOnly && (
        <div className="absolute z-50 top-full left-0 mt-1 w-52 bg-card border border-border rounded-lg shadow-xl py-1 max-h-64 overflow-y-auto">
          <div className="px-2 py-1.5">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full bg-main border border-border/50 rounded px-2 py-1 text-xs text-text-primary placeholder-text-secondary outline-none focus:border-accent/50"
            />
          </div>
          {filtered.map((user) => (
            <button
              key={user.id}
              onClick={() => { onChange(user.id); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-white/5 transition-colors"
            >
              {user.avatar_url ? (
                <img src={user.avatar_url} className="w-6 h-6 rounded-full" alt={user.name} />
              ) : (
                <div className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[10px] font-semibold shrink-0">
                  {getInitials(user.name)}
                </div>
              )}
              <span className="truncate">{user.name}</span>
            </button>
          ))}
          {truncated && (
            <div className="px-3 py-2 text-xs text-text-secondary">Refine a busca</div>
          )}
          {assigned && (
            <button
              onClick={() => { onChange(null); setOpen(false); }}
              className="w-full px-3 py-2 text-xs text-text-secondary hover:bg-white/5 transition-colors border-t border-border/50 mt-1"
            >
              Remover
            </button>
          )}
        </div>
      )}
    </div>
  );
}
